import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import {
  ICityDetail,
  ICitySimple,
  ITopTenCityDetail,
  IValuation,
  IPagination,
  IQualificationCity,
  IUserQualificationCity,
  IUserValuationCity,
  IValuationCity,
  Sidebar,
  IInfoRating,
} from '../models';
import { CityEndpoint } from '../endpoints/city';
import { catchError } from 'rxjs/operators';
import { Params, Router } from '@angular/router';
import { StringUtil } from '@shared/utils';

/**
 * Servicio que provee información y acciones para las ciudades
 */
@Injectable({
  providedIn: 'root',
})
export class City {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);

  constructor() {}

  /**
   * Gets sidebar information for cities
   * @returns Array of Sidebar objects with sidebar info
   */
  getSidebar(): Observable<Sidebar[]> {
    return this.http.get<Sidebar[]>(CityEndpoint.citiesSidebar);
  }

  /**
   * Retrieves a paginated list of cities based on the provided query parameters.
   *
   * @param params - The query parameters to filter and paginate the cities.
   * @returns An Observable emitting a paginated response containing an array of simple city objects.
   */
  getCities(params: Params): Observable<IPagination<ICitySimple[]>> {
    return this.http.get<IPagination<ICitySimple[]>>(CityEndpoint.cities, { params });
  }

  /**
   * Metodo para obtener las ciudades registradas en el back
   * @return url: Objeto que contiene la informacion de las ciudades
   */
  list(page = true): Observable<Array<ICitySimple>> {
    const url = `${CityEndpoint.cities}?pagination=false`;
    return this.http.get<Array<ICitySimple>>(url);
  }

  /**
   *Metodo que trae información especifica de una ciudad
   * @param id: valor de typo any que hace referencia a la ciudad
   * @return {ICityDetail}: objeto que tiene la información de la ciudad especifica
   */
  get(id: any): Observable<ICityDetail> {
    return this.http.get<ICityDetail>(StringUtil.format(CityEndpoint.city, id)).pipe(
      catchError((error) => {
        this.router.navigate(['pages/404']);
        return throwError('Page dont exist');
      }),
    );
  }

  /**
   * Function responsible for fetching the ratings and qualifications information of a specific city
   * @param cityId Id of the city
   * @returns Returns an observable with the ratings and qualifications information
   */
  getInfoRatings(cityId: number): Observable<IInfoRating> {
    return this.http.get<IInfoRating>(StringUtil.format(CityEndpoint.cityInfoRatings, cityId));
  }

  /**
   *Metodo que trae la valoraciones para una ciudad
   * @param city : parametro de tipo number que hace referencia al identificador de la ciudad
   * @param url: string que indica la página (los datos se páginan en back) de la cual se obtendran las valoraciones
   * @returns Objeto que contiene la lista de valoraciones
   */
  getValuations(city: number, url?: string): Observable<IPagination<Array<IValuation>>> {
    return this.http.get<IPagination<Array<IValuation>>>(
      !url ? StringUtil.format(CityEndpoint.cityValuations, city) : url,
    );
  }

  /**
   *Método que agrega una valoracion a una ciudad
   * @param valuation: parametro de tipo IValuationCity que contiene la infromación necesaria praa agregar la valoración
   */
  addValuation(valuation: IValuationCity): Observable<IValuationCity> {
    return this.http.post<IValuationCity>(CityEndpoint.valuationsRest, valuation);
  }

  /**
   *Método que edita una valoracion de una ciudad especificada
   * @param id: parametro que recibe el identificador de la ciudad
   * @param valuation: objeto de tipo IvaluacionCity que contiene la información necesaria para editar la valoración
   */
  editValuation(id: number, valuation: IValuationCity): Observable<IValuationCity> {
    return this.http.put<IValuationCity>(
      StringUtil.format(CityEndpoint.valuationsEdit, id),
      valuation,
    );
  }

  /**
   *Método que edita una valoracion de una ciudad específicada
   * @param id: parametro de tipo number que recibe el identificador de la ciudad
   * @param valuation: objeto de tipo IvaluacionCity que contiene la información necesaria para editar la valoración (no todos los campos son obligatorios)
   */
  patchValuation(id: number, valuation: Partial<IValuationCity>): Observable<IValuationCity> {
    return this.http.patch<IValuationCity>(
      StringUtil.format(CityEndpoint.valuationsEdit, id),
      valuation,
    );
  }

  /**
   *Método que elimina una valoración
   * @param id: parametro que recibe el identificador de la valoración a eliminar
   */
  deleteValuation(id: number): Observable<IValuationCity> {
    return this.http.delete<IValuationCity>(StringUtil.format(CityEndpoint.valuationsEdit, id));
  }

  /**
   *Método que agrega una calificación a una ciudad
   * @param qualification: objeto de tipo IQualificationCity que contiene la informacion necesaria para agregar la calificacion
   * @returns {IQualificationCity}: si se agrego correctamente el usuario retorna un objeto del tipo IQualificationCity o en su defecto error
   */
  addQualification(qualification: IQualificationCity): Observable<IQualificationCity> {
    return this.http.post<IQualificationCity>(CityEndpoint.qualificationsAdd, qualification);
  }

  /**
   *Método que edita la calificación para una ciudad
   * @param id: parametro que recibe el identificador de la calificación
   * @param qualification: parametro que recibe un objeto de tipo IQualificationCity con la información necesaria para editar la calificación.
   */
  editQualification(
    id: number,
    qualification: Partial<IQualificationCity>,
  ): Observable<IQualificationCity> {
    // al ser de tipo PUT requiere de toda la información de la calificación para editar la misma
    return this.http.put<IQualificationCity>(
      StringUtil.format(CityEndpoint.qualificationsEdit, id),
      qualification,
    );
  }

  /**
   *Método que edita la calificación de una ciudad
   * @param id: parametro que recibe el identificador de la calificación
   * @param qualification: parametro que recibe un objeto de tipo IQualificationCity con la información necesaria para editar la calificación.
   */
  patchQualification(
    id: number,
    qualification: Partial<IQualificationCity>,
  ): Observable<IQualificationCity> {
    // al ser PATCH no requiere que se envie toda la informacion que exige IQualificationCity, con la sola calificacion basta
    return this.http.patch<IQualificationCity>(
      StringUtil.format(CityEndpoint.qualificationsEdit, id),
      qualification,
    );
  }

  /**
   *Método que trae las valoraciones de una ciudad hechas por un usuario específico
   * @param city: parametro que hace referencia a la identificador de la ciudad
   * @param user: parametro que hace referencia al identificador del usuario
   */
  valuationsByUser(city: number, user: number): Observable<Array<IUserValuationCity>> {
    return this.http.get<Array<IUserValuationCity>>(
      StringUtil.format(CityEndpoint.valuationsByUser, city, user),
    );
  }

  /**
   *Metdodo que trae la calificacion que un usuario le ha hecho a una ciudad
   * @param city: hace referencia al identificador de la ciudad
   * @param user: hace referencia al identificador del usuario
   */
  qualificationByUser(city: number, user: number): Observable<IUserQualificationCity> {
    return this.http.get<IUserQualificationCity>(
      StringUtil.format(CityEndpoint.qualificationByUser, city, user),
    );
  }

  /**
   *Método que elimina una calificación
   * @param id: hace referencia al id de la calificación
   */
  deleteQualification(id: number): Observable<any> {
    return this.http.delete(StringUtil.format(CityEndpoint.qualificationsEdit, id));
  }

  /**
   * Método que retorna las ciudades que esten en el top 10 respectivo
   * @param url: url a la cual se le capturan los datos mediante una petición get
   * @param local_food: indica si debe o no aplicar el filtro de comida local
   */
  topten(url: string, local_food?: boolean): Observable<IPagination<Array<ITopTenCityDetail>>> {
    const uri = local_food ? `${url}?local_food=${1}` : url;
    return this.http.get<IPagination<Array<ITopTenCityDetail>>>(uri);
  }
}
